import React from "react";
import { Flex } from "antd";
import styled from "styled-components";
import { InfoTooltip, Skeleton, radius } from "@libertify/ui";
import { analyticsTheme } from "./analyticsTheme";

export type AnalyticsCardProps = {
  title: string;
  subtitle?: string;
  /** How to read the card, shown behind an info icon next to the title. */
  info?: string;
  /** Draws placeholders in place of the title and subtitle. */
  loading?: boolean;
  /** Shown next to the title, e.g. a tag saying what the card counts. */
  badge?: React.ReactNode;
  children: React.ReactNode;
};

/** The frame, title and subtitle shared by the analytics charts and panels. */
export const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  subtitle,
  info,
  loading = false,
  badge,
  children,
}) => {
  return (
    <StyledCard>
      {loading ? (
        <Flex vertical gap={8} style={{ marginBottom: 16 }}>
          <Skeleton variant="rectangular" width="40%" height={18} />
          <Skeleton variant="rectangular" width="55%" height={13} />
        </Flex>
      ) : (
        <Flex vertical gap={4} style={{ marginBottom: 8 }}>
          <Flex align="center" justify="space-between" gap={8}>
            <Flex align="center" wrap gap={8}>
              <StyledTitle>{title}</StyledTitle>
              {badge}
            </Flex>
            {info ? <InfoTooltip content={info} /> : null}
          </Flex>
          {subtitle ? <StyledSubtitle>{subtitle}</StyledSubtitle> : null}
        </Flex>
      )}
      {children}
    </StyledCard>
  );
};

// ─── Styled components ───

const StyledCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${analyticsTheme.cardBorder};
  border-radius: ${radius.box}px;
  padding: 20px 24px;
  background: ${analyticsTheme.cardBg};
`;

const StyledTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const StyledSubtitle = styled.span`
  font-size: 13px;
  font-weight: 300;
  color: ${analyticsTheme.textSubtitle};
`;
